import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/Shared/Table';
import {
  useAdminTenantStore,
  type CreateTenantResponse,
} from '../../../store/useAdminTenantStore';
import { useSubscriptionPlanStore } from '../../../store/useSubscriptionPlanStore';
import { BusinessPlan } from '../../../api/types/admin';
import TenantFormDialog from '../../Tenants/components/TenantFormDialog';
import TenantCreatedDialog from './TenantCreatedDialog';
import { formatDate } from '../../../utils/dateUtils';

function planColor(bp: number | undefined): 'gray' | 'blue' | 'green' | 'purple' {
  switch (bp) {
    case BusinessPlan.Starter:
      return 'blue';
    case BusinessPlan.Professional:
      return 'green';
    case BusinessPlan.Enterprise:
      return 'purple';
    default:
      return 'gray';
  }
}

export default function TenantsAdminPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { tenants, loading, error, fetchAll } = useAdminTenantStore();
  const { plans, fetchAll: fetchPlans } = useSubscriptionPlanStore();

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<'all' | number>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [createdResponse, setCreatedResponse] = useState<CreateTenantResponse | null>(null);

  useEffect(() => {
    fetchAll();
    if (plans.length === 0) fetchPlans();
  }, [fetchAll, fetchPlans, plans.length]);

  const filtered = useMemo(() => {
    return tenants.filter((tn) => {
      if (planFilter !== 'all' && tn.businessPlan !== planFilter) return false;
      if (statusFilter === 'active' && !tn.isActive) return false;
      if (statusFilter === 'inactive' && tn.isActive) return false;
      if (search) {
        const s = search.toLowerCase();
        const name = (tn.name || '').toLowerCase();
        const domain = (tn.domain || '').toLowerCase();
        if (!name.includes(s) && !domain.includes(s)) return false;
      }
      return true;
    });
  }, [tenants, planFilter, statusFilter, search]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('admin:tenants.title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('admin:tenants.subtitle')}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              onClick={() => setCreateOpen(true)}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto justify-center"
            >
              {t('admin:tenants.actions.create')}
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              {t('common:actions.search')}
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin:tenants.filters.searchPlaceholder')}
                className="w-full pl-8 pr-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              {t('admin:tenants.filters.plan')}
            </label>
            <select
              value={String(planFilter)}
              onChange={(e) =>
                setPlanFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:tenants.filters.all')}</option>
              <option value={BusinessPlan.Free}>{t('admin:tenants.planNames.0')}</option>
              <option value={BusinessPlan.Starter}>{t('admin:tenants.planNames.1')}</option>
              <option value={BusinessPlan.Professional}>{t('admin:tenants.planNames.2')}</option>
              <option value={BusinessPlan.Enterprise}>{t('admin:tenants.planNames.3')}</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              {t('admin:tenants.filters.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
              }
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:tenants.filters.all')}</option>
              <option value="active">{t('admin:tenants.filters.active')}</option>
              <option value="inactive">{t('admin:tenants.filters.inactive')}</option>
            </select>
          </div>
        </div>
      </Card>

      {error ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('admin:tenants.errors.fetchFailed')}
            onRetry={fetchAll}
          />
        </Card>
      ) : loading && tenants.length === 0 ? (
        <Card>
          <p className="text-[13px] text-slate-500 text-center py-6">
            {t('common:actions.loading')}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState title={t('admin:tenants.empty')} />
        </Card>
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('admin:tenants.fields.name')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('admin:tenants.fields.domain')}</TableHeader>
                <TableHeader>{t('admin:tenants.fields.plan')}</TableHeader>
                <TableHeader>{t('admin:tenants.fields.status')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('admin:tenants.fields.createdAt')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((tn) => (
                <TableRow key={tn.id}>
                  <TableCell>
                    <Link
                      to={`/admin/tenants/${tn.id}`}
                      className="font-medium text-slate-900 hover:text-[#00A871] transition-colors"
                    >
                      {tn.name}
                    </Link>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-600 font-mono text-[12px]">
                    {tn.domain || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge color={planColor(tn.businessPlan)}>
                      {t(`admin:tenants.planNames.${tn.businessPlan ?? 0}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {tn.isActive ? (
                      <Badge variant="success">{t('admin:tenants.filters.active')}</Badge>
                    ) : (
                      <Badge variant="default">{t('admin:tenants.filters.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-slate-600">
                    {formatDate(tn.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <TenantFormDialog
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          fetchAll();
        }}
        tenant={null}
        onCreated={(response) => setCreatedResponse(response)}
      />

      <TenantCreatedDialog
        response={createdResponse}
        onClose={() => setCreatedResponse(null)}
      />
    </div>
  );
}
